import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";
import { useEffect, useState } from "react";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import BusinessTable, { RowWithOptionalClick } from "../../../table";
import React from "react";
import classNames from "classnames";
import { getOrganizationTiers } from "../../../../../setup/api/services/organization";
import { EPaymentBrand } from "../../../../../setup/api/services/contract/models";
import { matchPath, useLocation } from "react-router-dom";
import { ROUTES } from "../../../../../setup/constants";

interface TiersInformationProps {
  organization: IOrganizations | undefined;
}

// Define tier information structure
interface TierInfo extends RowWithOptionalClick {
  name: string;
  from: number | string;
  to: number | string;
  bankTransfer: string;
  visa: string;
  mastercard: string;
  amex: string;
  mada: string;
  tabby: string;
}

const TiersInformation = ({ organization }: TiersInformationProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [tiers, setTiers] = useState<TierInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Define table columns
  const columns = [
    { type: "tierName", value: t("lbl_tier_name") },
    { type: "from", value: t("lbl_from") },
    { type: "to", value: t("lbl_to") },
    {
      type: "bankTransfer",
      value: t("lbl_bank_transfer_amount"),
    },
    { type: "visa", value: t("lbl_visa") },
    { type: "mastercard", value: t("lbl_master_card") },
    { type: "amex", value: t("lbl_amex") },
    { type: "mada", value: t("lbl_mada") },
    { type: "tabby", value: t("lbl_tabby_amount") },
  ];

  const isCreateMatch = matchPath(
    { path: ROUTES.CREATE_COMPLEX_CONTRACT },
    location.pathname
  );

  useEffect(() => {
    const fetchOrganizationTiers = async () => {
      if (!organization || !!tiers?.length) return;
      setIsLoading(true);
      try {
        const response = await getOrganizationTiers(organization?.code);

        setTiers(
          response?.data?.tiers?.map((tier) => ({
            name: tier?.name,
            from: tier?.from,
            to: tier?.to || "-",
            bankTransfer:
              !tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.BANK_TRANSFER
              )?.fees ||
              tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.BANK_TRANSFER
              )?.fees === 0
                ? "-"
                : tier?.paymentTypes?.find(
                    (type) => type?.brand === EPaymentBrand.BANK_TRANSFER
                  )?.feeType === "PERCENTAGE"
                ? `${(
                    (tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.BANK_TRANSFER
                    )?.fees || 0) * 100
                  ).toFixed(1)}%`
                : `${
                    tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.BANK_TRANSFER
                    )?.fees
                  } ${t("lbl_saudi_currency")}`,
            visa:
              !tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.VISA
              )?.fees ||
              tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.VISA
              )?.fees === 0
                ? "-"
                : tier?.paymentTypes?.find(
                    (type) => type?.brand === EPaymentBrand.VISA
                  )?.feeType === "PERCENTAGE"
                ? `${(
                    (tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.VISA
                    )?.fees || 0) * 100
                  ).toFixed(1)}%`
                : `${
                    tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.VISA
                    )?.fees
                  } ${t("lbl_saudi_currency")}`,
            mastercard:
              !tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.MASTER
              )?.fees ||
              tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.MASTER
              )?.fees === 0
                ? "-"
                : tier?.paymentTypes?.find(
                    (type) => type?.brand === EPaymentBrand.MASTER
                  )?.feeType === "PERCENTAGE"
                ? `${(
                    (tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.MASTER
                    )?.fees || 0) * 100
                  ).toFixed(1)}%`
                : `${
                    tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.MASTER
                    )?.fees
                  } ${t("lbl_saudi_currency")}`,
            amex:
              !tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.AMEX
              )?.fees ||
              tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.AMEX
              )?.fees === 0
                ? "-"
                : tier?.paymentTypes?.find(
                    (type) => type?.brand === EPaymentBrand.AMEX
                  )?.feeType === "PERCENTAGE"
                ? `${(
                    (tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.AMEX
                    )?.fees || 0) * 100
                  ).toFixed(1)}%`
                : `${
                    tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.AMEX
                    )?.fees
                  } ${t("lbl_saudi_currency")}`,
            mada:
              !tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.MADA
              )?.fees ||
              tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.MADA
              )?.fees === 0
                ? "-"
                : tier?.paymentTypes?.find(
                    (type) => type?.brand === EPaymentBrand.MADA
                  )?.feeType === "PERCENTAGE"
                ? `${(
                    (tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.MADA
                    )?.fees || 0) * 100
                  ).toFixed(1)}%`
                : `${
                    tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.MADA
                    )?.fees
                  } ${t("lbl_saudi_currency")}`,
            tabby:
              !tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.TABBY
              )?.fees ||
              tier?.paymentTypes?.find(
                (type) => type?.brand === EPaymentBrand.TABBY
              )?.fees === 0
                ? "-"
                : tier?.paymentTypes?.find(
                    (type) => type?.brand === EPaymentBrand.TABBY
                  )?.feeType === "PERCENTAGE"
                ? `${(
                    (tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.TABBY
                    )?.fees || 0) * 100
                  ).toFixed(1)}%`
                : `${
                    tier?.paymentTypes?.find(
                      (type) => type?.brand === EPaymentBrand.TABBY
                    )?.fees
                  } ${t("lbl_saudi_currency")}`,
          }))
        );
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizationTiers();
  }, [organization, t, tiers?.length]);

  if (isLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner />
      </div>
    );
  }

  // Render cell based on column type and data
  const renderCell = (columnType: string, row: TierInfo) => {
    switch (columnType) {
      case "tierName":
        return <span className={styles.tierName}>{row.name}</span>;
      case "from":
        return (
          <span className={styles.amountValue}>
            {row?.from?.toLocaleString()}
          </span>
        );
      case "to":
        return (
          <span
            className={classNames(
              styles.amountValue,
              styles.activeAmountValue
            )}>
            {row?.to?.toLocaleString()}
          </span>
        );
      case "bankTransfer":
        return (
          <span
            className={styles.percentValue}
            style={
              row.bankTransfer.includes("%") ? undefined : { direction: "rtl" }
            }>
            {row.bankTransfer}
          </span>
        );
      case "visa":
        return (
          <span
            className={styles.percentValue}
            style={row.visa.includes("%") ? undefined : { direction: "rtl" }}>
            {row.visa}
          </span>
        );
      case "mastercard":
        return (
          <span
            className={styles.percentValue}
            style={
              row.mastercard.includes("%") ? undefined : { direction: "rtl" }
            }>
            {row.mastercard}
          </span>
        );
      case "amex":
        return (
          <span
            className={styles.percentValue}
            style={row.amex.includes("%") ? undefined : { direction: "rtl" }}>
            {row.amex}
          </span>
        );
      case "mada":
        return (
          <span
            className={styles.percentValue}
            style={row.mada.includes("%") ? undefined : { direction: "rtl" }}>
            {row.mada}
          </span>
        );
      case "tabby":
        return (
          <span
            className={styles.percentValue}
            style={row.tabby.includes("%") ? undefined : { direction: "rtl" }}>
            {row.tabby}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <h3
          className={styles.title}
          style={{ fontSize: isCreateMatch ? "24px" : "18px" }}>
          {isCreateMatch
            ? t("lbl_payment_methods_fees")
            : t("lbl_tiers_info_title")}
        </h3>
        <p className={styles.subtitle}>{t("lbl_tiers_info_subtitle")}</p>
      </div>
      <div className={styles.tableContainer}>
        <TiersBusinessTable
          columns={columns}
          rows={tiers}
          renderCell={(columnType, row) =>
            renderCell(columnType, row as TierInfo)
          }
        />
      </div>
    </div>
  );
};

// Custom wrapper for BusinessTable specifically for TiersInformation
const TiersBusinessTable = ({
  columns,
  rows,
  renderCell,
}: {
  columns: { type: string; value: string }[];
  rows: TierInfo[];
  renderCell: (
    column: string,
    row: TierInfo,
    rowIndex: number
  ) => React.ReactNode;
}) => {
  return (
    <div className={styles.tiersTableWrapper}>
      <BusinessTable
        columns={columns}
        rows={rows}
        renderCell={(column, row, rowIndex) =>
          renderCell(column, row as TierInfo, rowIndex)
        }
      />
    </div>
  );
};

export default TiersInformation;
